// src/components/MonthlySummary.js
//
// Chunk 9b — full redesign. No more tabs, no decorative emoji, no
// score badges. A focused 30-second reflection card.
//
// Sections (top to bottom, scrollable):
//   1. Hero verdict   — total spent + headline (saved $X / over by $Y / no plan)
//   2. Top categories — top 3 with bar visualization
//   3. Bills          — closing the loop on what was paid vs missed
//   4. Goals & debts  — did the on-track promises land
//   5. One observation — anomaly callout, only when meaningful
//
// User-set emojis on category icons are preserved (they belong to the
// user). The chrome itself uses Fraunces, plum/cream, and Lucide only
// for the close X.
//
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { emitDataEvent, EVENTS } from '../lib/dataEvents'
import { recordDebtBalance } from '../lib/debtHistory'
import { useCurrency } from '../lib/CurrencyContext'
import { theme } from '../lib/theme'

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

export default function MonthlySummary({ onDismiss }) {
  const { formatCurrency } = useCurrency()
  const now = new Date()

  // Default to last month
  const defaultMonth = now.getMonth() === 0 ? 12 : now.getMonth()
  const defaultYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()

  const [selectedMonth, setSelectedMonth] = useState(defaultMonth)
  const [selectedYear, setSelectedYear] = useState(defaultYear)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const selectedMonthName = MONTH_NAMES[selectedMonth - 1]

  // Build last 12 months for selector
  const monthOptions = []
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    monthOptions.push({
      month: d.getMonth() + 1,
      year: d.getFullYear(),
      label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
    })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [selectedMonth, selectedYear])

  const fetchData = async () => {
    setLoading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setLoading(false); return }

      // Household-pool the reads (Chunk 8 follow-up) — same lens as ThisMonth,
      // so partnered households see identical numbers. Writes still target the
      // owning user's row (see the Leftover Sweep / simple-debt paths).
      const { data: link } = await supabase.from('partner_links')
        .select('user_id_1, user_id_2')
        .or(`user_id_1.eq.${user.id},user_id_2.eq.${user.id}`)
        .eq('is_active', true).maybeSingle()
      const partnerId = link
        ? (link.user_id_1 === user.id ? link.user_id_2 : link.user_id_1)
        : null
      const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

      const monthStr = String(selectedMonth).padStart(2, '0')
      const lastDay = new Date(selectedYear, selectedMonth, 0).getDate()
      const startDate = `${selectedYear}-${monthStr}-01`
      const endDate = `${selectedYear}-${monthStr}-${lastDay}`

      // Get all transactions for selected month
      const { data: txs } = await supabase
        .from('transactions')
        .select('*, budget_categories(id, name, icon, parent_id), bills(id, name)')
        .in('user_id', userIds)
        .eq('type', 'expense')
        .gte('transaction_date', startDate)
        .lte('transaction_date', endDate)
        .order('transaction_date', { ascending: false })

      const transactions = txs || []
      const totalSpent = transactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)

      // Group by category, rolling subcategories up to parents
      const catMap = {}
      transactions.forEach(tx => {
        const cat = tx.budget_categories
        if (!cat) {
          const key = 'uncategorized'
          if (!catMap[key]) catMap[key] = { name: 'Uncategorized', icon: '', spent: 0, count: 0 }
          catMap[key].spent += parseFloat(tx.amount || 0)
          catMap[key].count++
          return
        }
        const key = cat.parent_id || cat.id
        if (!catMap[key]) catMap[key] = { name: cat.parent_id ? '' : cat.name, icon: cat.icon, spent: 0, count: 0, id: key }
        catMap[key].spent += parseFloat(tx.amount || 0)
        catMap[key].count++
      })

      // Fill in parent names + limits
      const allCatIds = Object.keys(catMap).filter(k => k !== 'uncategorized')
      if (allCatIds.length > 0) {
        const { data: parentCats } = await supabase
          .from('budget_categories')
          .select('id, name, icon, limit_amount')
          .in('id', allCatIds)
        if (parentCats) {
          parentCats.forEach(pc => {
            if (catMap[pc.id]) {
              catMap[pc.id].name = pc.name
              catMap[pc.id].icon = pc.icon
              catMap[pc.id].limit = parseFloat(pc.limit_amount || 0)
            }
          })
        }
      }

      const categoryResults = Object.values(catMap)
        .filter(c => c.name)
        .sort((a, b) => b.spent - a.spent)

      // Get budget for this month
      const { data: budget } = await supabase
        .from('budgets').select('id')
        .in('user_id', userIds)
        .eq('month', selectedMonth)
        .eq('year', selectedYear)
        .eq('is_active', true)
        .order('created_at').limit(1).maybeSingle()

      let categoryResultsWithLimits = categoryResults
      let nailed = 0
      let totalCats = 0
      let allBudgetCats = []

      if (budget) {
        const { data: budgetCats } = await supabase
          .from('budget_categories')
          .select('id, name, limit_amount, parent_id')
          .in('user_id', userIds)
          .eq('budget_id', budget.id)
        allBudgetCats = budgetCats || []
        const parentCats = allBudgetCats.filter(b => !b.parent_id)
        categoryResultsWithLimits = categoryResults.map(cat => {
          const bc = parentCats.find(b => b.id === cat.id)
          const limit = bc ? parseFloat(bc.limit_amount || 0) : 0
          const underBudget = limit > 0 && cat.spent <= limit
          if (limit > 0) { totalCats++; if (underBudget) nailed++ }
          return { ...cat, limit, underBudget }
        })
      }

      // Get bills that should have been paid this month
      const { data: bills } = await supabase
        .from('bills').select('id, name, amount, frequency, due_day')
        .in('user_id', userIds)
        .eq('is_active', true)

      // Determine which bills had a transaction with bill_id this month
      const paidBillIds = new Set(
        transactions.filter(tx => tx.bill_id).map(tx => tx.bill_id)
      )
      const expectedBills = (bills || []).filter(b => b.frequency !== 'one_time')
      const missedBills = expectedBills.filter(b => !paidBillIds.has(b.id))
      const paidBills = expectedBills.filter(b => paidBillIds.has(b.id))

      // Get goals & debts (with monthly contribution targets)
      const { data: goals } = await supabase
        .from('savings_goals').select('*')
        .in('user_id', userIds)
      const { data: debts } = await supabase
        .from('debts').select('*')
        .in('user_id', userIds)

      // For each goal/debt, count how much was logged toward its category this month
      const goalsWithProgress = (goals || []).map(g => {
        const targetMonthly = parseFloat(g.monthly_contribution || 0)
        const txForGoal = transactions.filter(tx =>
          tx.budget_categories?.name === g.name ||
          (g.category_id && tx.category_id === g.category_id)
        )
        const paidThisMonth = txForGoal.reduce((s, tx) => s + parseFloat(tx.amount || 0), 0)
        return { ...g, targetMonthly, paidThisMonth, hit: targetMonthly > 0 && paidThisMonth >= targetMonthly }
      }).filter(g => g.targetMonthly > 0)

      const debtsWithProgress = (debts || []).map(d => {
        const targetMonthly = parseFloat(d.monthly_payment || 0)
        const txForDebt = transactions.filter(tx =>
          tx.budget_categories?.name === d.name ||
          (d.category_id && tx.category_id === d.category_id)
        )
        const paidThisMonth = txForDebt.reduce((s, tx) => s + parseFloat(tx.amount || 0), 0)
        return { ...d, targetMonthly, paidThisMonth, hit: targetMonthly > 0 && paidThisMonth >= targetMonthly }
      }).filter(d => d.targetMonthly > 0)

      // Compute one observation — anomaly detection vs 6-month avg
      let observation = null
      if (categoryResultsWithLimits.length > 0 && transactions.length > 5) {
        // Get last 6 months of category spending for top category
        const top = categoryResultsWithLimits[0]
        if (top && top.id) {
          const sixMonthsAgo = new Date(selectedYear, selectedMonth - 7, 1)
          const sixStart = `${sixMonthsAgo.getFullYear()}-${String(sixMonthsAgo.getMonth() + 1).padStart(2,'0')}-01`
          const { data: pastTxs } = await supabase
            .from('transactions')
            .select('amount, transaction_date, category_id, budget_categories!inner(parent_id)')
            .in('user_id', userIds)
            .eq('type', 'expense')
            .gte('transaction_date', sixStart)
            .lt('transaction_date', startDate)
          if (pastTxs && pastTxs.length > 5) {
            // Filter to those in same parent category
            const sameCat = pastTxs.filter(tx =>
              tx.category_id === top.id ||
              tx.budget_categories?.parent_id === top.id
            )
            // Group by month
            const byMonth = {}
            sameCat.forEach(tx => {
              const k = tx.transaction_date.substring(0, 7) // YYYY-MM
              byMonth[k] = (byMonth[k] || 0) + parseFloat(tx.amount || 0)
            })
            const months = Object.values(byMonth)
            if (months.length >= 2) {
              const avg = months.reduce((s, v) => s + v, 0) / months.length
              if (avg > 0 && top.spent > avg * 1.3) {
                const pct = Math.round(((top.spent - avg) / avg) * 100)
                observation = {
                  text: `You spent ${pct}% more on ${top.name} than your ${months.length}-month average.`,
                  tone: 'caution',
                }
              } else if (avg > 0 && top.spent < avg * 0.7) {
                const pct = Math.round(((avg - top.spent) / avg) * 100)
                observation = {
                  text: `You spent ${pct}% less on ${top.name} than your ${months.length}-month average.`,
                  tone: 'positive',
                }
              }
            }
          }
        }
      }

      // Allocated total — recomputed from line items (triage #5), using the
      // SAME formula as ThisMonth.js's budgetTotal so the two never drift. No
      // longer reads budget.total_amount. variance = allocated − spent
      // (positive = came in under plan / money left over).
      const baseTotal = allBudgetCats
        .filter(cat => cat.parent_id)
        .reduce((s, cat) => s + parseFloat(cat.limit_amount || 0), 0)
      const goalsAlloc = (goals || []).reduce((s, g) => s + parseFloat(g.monthly_contribution || 0), 0)
      const debtsAlloc = (debts || []).reduce((s, d) => s + parseFloat(d.monthly_payment || 0), 0)
      const billsAlloc = (bills || []).reduce((s, b) => s + parseFloat(b.amount || 0), 0)
      const totalAllocated = baseTotal + goalsAlloc + debtsAlloc + billsAlloc
      const variance = budget ? totalAllocated - totalSpent : null

      setData({
        transactions,
        totalSpent,
        totalAllocated,
        hasBudget: !!budget,
        variance,
        categoryResults: categoryResultsWithLimits,
        nailed, totalCats,
        paidBills, missedBills, expectedBills,
        goals: goalsWithProgress,
        debts: debtsWithProgress,
        allGoals: goals || [],   // raw — for the Leftover Sweep goal picker
        allDebts: debts || [],   // raw — for the simple-mode debt reminder + debt sweep
        budgetCategories: allBudgetCats,
        observation,
        selectedMonth, selectedYear, selectedMonthName,
        empty: transactions.length === 0,
      })
    } catch (e) {
      console.error('MonthlySummary fetch error:', e)
      setData({ empty: true, selectedMonthName })
    }
    setLoading(false)
  }

  // ─── Render ─────────────────────────────────────────────

  const renderHero = () => {
    if (!data || data.empty) return null
    const { totalSpent, hasBudget, variance, totalAllocated } = data
    // variance = allocated − spent (positive = saved). Zero Rule ±$0.50 band.
    let kicker, headline, kickerColor, gradient
    if (!hasBudget) {
      kicker = 'Total spending'
      headline = `${data.transactions.length} transaction${data.transactions.length === 1 ? '' : 's'} this month`
      kickerColor = c.inkMuted
      gradient = null
    } else if (variance > 0.5) {
      kicker = `Saved ${formatCurrency(variance)}`
      headline = `${formatCurrency(totalSpent)} spent · ${formatCurrency(totalAllocated)} planned`
      kickerColor = c.green
      gradient = 'linear-gradient(120deg, #16A34A 0%, #1E1040 100%)'
    } else if (variance < -0.5) {
      kicker = `Over by ${formatCurrency(Math.abs(variance))}`
      headline = `${formatCurrency(totalSpent)} spent · ${formatCurrency(totalAllocated)} planned`
      kickerColor = c.rose
      gradient = 'linear-gradient(120deg, #BE185D 0%, #1E1040 100%)'
    } else {
      kicker = 'Right on plan'
      headline = `${formatCurrency(totalSpent)} spent · ${formatCurrency(totalAllocated)} planned`
      kickerColor = c.green
      gradient = null
    }
    const heroValueStyle = gradient
      ? { ...styles.heroValue, background: gradient, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' }
      : styles.heroValue
    return (
      <div style={styles.hero}>
        <div style={{ ...styles.heroKicker, color: kickerColor }}>{kicker}</div>
        <div style={heroValueStyle}>
          {formatCurrency(totalSpent)}
        </div>
        <div style={styles.heroSub}>{headline}</div>
      </div>
    )
  }

  const renderTopCategories = () => {
    if (!data || data.empty || data.categoryResults.length === 0) return null
    const top = data.categoryResults.slice(0, 3)
    return (
      <section style={styles.section}>
        <div style={styles.sectionLabel}>Where it went</div>
        <div style={styles.catList}>
          {top.map((cat, i) => {
            const pct = data.totalSpent > 0 ? (cat.spent / data.totalSpent) * 100 : 0
            return (
              <div key={i} style={styles.catRow}>
                <div style={styles.catRowTop}>
                  <span style={styles.catName}>{cat.name}</span>
                  <span style={styles.catAmount}>{formatCurrency(cat.spent)}</span>
                </div>
                <div style={styles.catBar}>
                  <div style={{
                    ...styles.catBarFill,
                    width: `${Math.min(pct, 100)}%`,
                    background: cat.limit > 0 && !cat.underBudget ? theme.colors.rose : theme.colors.green,
                  }} />
                </div>
                <div style={styles.catMeta}>
                  {cat.limit > 0 ? (
                    cat.underBudget
                      ? <>{formatCurrency(cat.limit - cat.spent)} under plan</>
                      : <>{formatCurrency(cat.spent - cat.limit)} over plan</>
                  ) : (
                    <>{Math.round(pct)}% of total · {cat.count} transaction{cat.count === 1 ? '' : 's'}</>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  const renderBills = () => {
    if (!data || data.expectedBills.length === 0) return null
    const allPaid = data.missedBills.length === 0
    return (
      <section style={styles.section}>
        <div style={styles.sectionLabel}>Bills</div>
        {allPaid ? (
          <div style={styles.billsAllPaid}>
            All {data.expectedBills.length} recurring bill{data.expectedBills.length === 1 ? '' : 's'} paid this month.
          </div>
        ) : (
          <>
            <div style={styles.billsMissed}>
              {data.missedBills.length} bill{data.missedBills.length === 1 ? '' : 's'} not yet logged as paid:
            </div>
            <div style={styles.missedList}>
              {data.missedBills.map(b => (
                <div key={b.id} style={styles.missedRow}>
                  <span style={styles.missedName}>{b.name}</span>
                  <span style={styles.missedAmount}>{formatCurrency(b.amount)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    )
  }

  const renderGoalsDebts = () => {
    if (!data) return null
    const items = [...data.goals, ...data.debts]
    if (items.length === 0) return null
    return (
      <section style={styles.section}>
        <div style={styles.sectionLabel}>Goals & debts</div>
        <div style={styles.gdList}>
          {items.map((it, i) => {
            const pct = it.targetMonthly > 0
              ? Math.min((it.paidThisMonth / it.targetMonthly) * 100, 100)
              : 0
            return (
              <div key={i} style={styles.gdRow}>
                <div style={styles.gdTop}>
                  <span style={styles.gdName}>{it.name}</span>
                  <span style={{
                    ...styles.gdStatus,
                    color: it.hit ? theme.colors.green : theme.colors.orange,
                  }}>
                    {it.hit ? 'Hit' : 'Short'}
                  </span>
                </div>
                <div style={styles.catBar}>
                  <div style={{
                    ...styles.catBarFill,
                    width: `${pct}%`,
                    background: it.hit ? theme.colors.green : theme.colors.orange,
                  }} />
                </div>
                <div style={styles.gdMeta}>
                  {formatCurrency(it.paidThisMonth)} of {formatCurrency(it.targetMonthly)}
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  const renderObservation = () => {
    if (!data || !data.observation) return null
    const o = data.observation
    return (
      <section style={styles.section}>
        <div style={styles.sectionLabel}>One thing to notice</div>
        <div style={{
          ...styles.observation,
          ...(o.tone === 'caution' ? styles.observationCaution : styles.observationPositive),
        }}>
          {o.text}
        </div>
      </section>
    )
  }

  return (
    <div
      style={styles.overlay}
      onClick={(e) => { if (e.target === e.currentTarget) onDismiss() }}
      role="dialog"
      aria-modal="true"
    >
      <div style={styles.modal}>

        {/* Header — plum, with month selector */}
        <header style={styles.header}>
          <div style={styles.headerInner}>
            <div>
              <div style={styles.headerKicker}>Month review</div>
              <select
                style={styles.monthSelect}
                value={`${selectedMonth}-${selectedYear}`}
                onChange={(e) => {
                  const [m, y] = e.target.value.split('-')
                  setSelectedMonth(parseInt(m))
                  setSelectedYear(parseInt(y))
                }}
                aria-label="Select month"
              >
                {monthOptions.map(o => (
                  <option key={`${o.month}-${o.year}`} value={`${o.month}-${o.year}`}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button" className="btn-compact"
              style={styles.closeBtn}
              onClick={onDismiss}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </header>

        {/* Body */}
        <div style={styles.body}>
          {loading ? (
            <div style={styles.loadingWrap}>
              <span style={styles.loadingText}>Loading {selectedMonthName}…</span>
            </div>
          ) : data?.empty ? (
            <div style={styles.empty}>
              <div style={styles.emptyTitle}>Nothing logged for {selectedMonthName} {selectedYear}</div>
              <div style={styles.emptyText}>
                Try a different month, or start adding transactions on Log.
              </div>
            </div>
          ) : (
            <>
              {renderHero()}
              {renderTopCategories()}
              {renderBills()}
              {renderGoalsDebts()}
              {renderObservation()}
              <SimpleDebtReminder key={`sd-${data.selectedMonth}-${data.selectedYear}`} debts={data.allDebts || []} formatCurrency={formatCurrency} />
              <LeftoverSweep key={`sw-${data.selectedMonth}-${data.selectedYear}`} data={data} formatCurrency={formatCurrency} onDone={onDismiss} />
            </>
          )}
        </div>

        {/* Footer */}
        <footer style={styles.footer}>
          {!loading && !data?.empty && (
            <Link to="/report" style={styles.footerSecondary} onClick={onDismiss}>
              See full report
            </Link>
          )}
          <button
            type="button"
            style={styles.footerPrimary}
            onClick={onDismiss}
          >
            Got it
          </button>
        </footer>
      </div>
    </div>
  )
}

// ─── Simple-mode debt interest reminder (Chunk 8) ───────────
// Simple-mode debts don't accrue interest automatically (Chunk 6), so month-end
// is the moment to fold in any interest from the latest statement. One row per
// simple-mode debt with a positive balance; skipping leaves the balance as-is.
function SimpleDebtReminder({ debts, formatCurrency }) {
  const qualifying = (debts || []).filter(
    d => d.is_simple_mode === true && parseFloat(d.current_balance || 0) > 0
  )
  const [values, setValues] = useState(() =>
    Object.fromEntries(qualifying.map(d => [d.id, String(parseFloat(d.current_balance || 0))]))
  )
  const [savingId, setSavingId] = useState(null)
  const [savedIds, setSavedIds] = useState({})
  const [dismissed, setDismissed] = useState(false)

  if (qualifying.length === 0 || dismissed) return null

  const update = async (debt) => {
    const newVal = parseFloat(values[debt.id])
    const cur = parseFloat(debt.current_balance || 0)
    if (isNaN(newVal) || Math.abs(newVal - cur) < 0.005) {
      setSavedIds(s => ({ ...s, [debt.id]: 'nochange' }))
      return
    }
    setSavingId(debt.id)
    await supabase.from('debts')
      .update({ current_balance: newVal, updated_at: new Date().toISOString() })
      .eq('id', debt.id)
    await recordDebtBalance(debt.id, newVal, 'manual_update')
    emitDataEvent(EVENTS.DEBT_UPDATED)
    setSavingId(null)
    setSavedIds(s => ({ ...s, [debt.id]: 'updated' }))
  }

  return (
    <section style={styles.section}>
      <div style={styles.sectionLabel}>Simple debt balances</div>
      <div style={styles.sweepSub}>
        These debts don’t track interest automatically. Any balance updates from your latest statements?
      </div>
      <div style={styles.simpleDebtList}>
        {qualifying.map(d => (
          <div key={d.id} style={styles.simpleDebtRow}>
            <div style={styles.simpleDebtName}>{d.name}</div>
            <div style={styles.simpleDebtControls}>
              <span style={styles.simpleDebtCur}>Current {formatCurrency(parseFloat(d.current_balance || 0))}</span>
              <input
                type="number" step="0.01"
                style={styles.simpleDebtInput}
                value={values[d.id] ?? ''}
                onChange={e => setValues(v => ({ ...v, [d.id]: e.target.value }))}
                aria-label={`New balance for ${d.name}`}
              />
              <button
                type="button"
                style={savedIds[d.id] === 'updated' ? styles.simpleDebtBtnDone : styles.simpleDebtBtn}
                disabled={savingId === d.id}
                onClick={() => update(d)}
              >
                {savedIds[d.id] === 'updated' ? 'Updated' : savingId === d.id ? '…' : 'Update'}
              </button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" style={styles.skipAllBtn} onClick={() => setDismissed(true)}>
        Skip all — no updates
      </button>
    </section>
  )
}

// ─── Leftover Sweep (Chunk 8) ───────────────────────────────
// Shown only when the month came in under plan by more than $0.50 (spec §4).
// Lets the user redirect the leftover: emergency fund (default), any unmet
// goal, roll into next month's category, debt principal, or leave it.
function LeftoverSweep({ data, formatCurrency, onDone }) {
  const amount = data.variance
  const show = data.hasBudget && amount > 0.5

  const unmetGoals = (data.allGoals || []).filter(
    g => parseFloat(g.current_amount || 0) < parseFloat(g.target_amount || 0)
  )
  const emergencyFund = unmetGoals.find(g => g.is_emergency_fund) || null
  const debtOptions = (data.allDebts || []).filter(d => parseFloat(d.current_balance || 0) > 0)

  // Categories the user allocated to this month (child leaves with a limit),
  // excluding the debt/goal system buckets.
  const catOptions = (() => {
    const cats = data.budgetCategories || []
    const parentName = {}
    cats.forEach(c => { if (!c.parent_id) parentName[c.id] = c.name })
    const EXCLUDE = new Set(['debt payments', 'savings goals'])
    return cats
      .filter(c => c.parent_id && parseFloat(c.limit_amount || 0) > 0)
      .filter(c => !EXCLUDE.has((parentName[c.parent_id] || '').toLowerCase().trim()))
      .map(c => ({ id: c.id, name: c.name, parent_id: c.parent_id, parentName: parentName[c.parent_id] || '' }))
      .sort((a, b) => `${a.parentName}${a.name}`.localeCompare(`${b.parentName}${b.name}`))
  })()

  const [choice, setChoice] = useState(emergencyFund ? 'emergency' : null)
  const [goalId, setGoalId] = useState(unmetGoals[0]?.id || '')
  const [catId, setCatId] = useState(catOptions[0]?.id || '')
  const [debtId, setDebtId] = useState(debtOptions[0]?.id || '')
  const [applying, setApplying] = useState(false)

  if (!show) return null

  const options = [
    ...(emergencyFund ? [{ key: 'emergency', label: `Send to ${emergencyFund.name}` }] : []),
    ...(unmetGoals.length > 0 ? [{ key: 'goal', label: 'Send to a savings goal' }] : []),
    ...(catOptions.length > 0 ? [{ key: 'category', label: 'Roll into next month’s category' }] : []),
    ...(debtOptions.length > 0 ? [{ key: 'debt', label: 'Send to debt principal' }] : []),
    { key: 'leave', label: 'Leave it — just record' },
  ]

  const apply = async () => {
    if (!choice || applying) return
    setApplying(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (choice === 'emergency' && emergencyFund) {
        await bumpGoal(emergencyFund.id, amount)
      } else if (choice === 'goal' && goalId) {
        await bumpGoal(goalId, amount)
      } else if (choice === 'debt' && debtId) {
        await sweepToDebt(debtId, amount)
      } else if (choice === 'category' && catId) {
        const chosen = catOptions.find(o => o.id === catId)
        if (chosen && user) await rollToNextMonth(user.id, chosen, amount, data.selectedMonth, data.selectedYear)
      }
      // 'leave' → no write
    } catch (e) {
      console.error('Leftover sweep error:', e)
    }
    setApplying(false)
    onDone && onDone()
  }

  return (
    <section style={styles.section}>
      <div style={styles.sectionLabel}>Leftover</div>
      <div style={styles.sweepHead}>You have {formatCurrency(amount)} left over from {data.selectedMonthName}</div>
      <div style={styles.sweepSub}>Where should it go?</div>
      <div style={styles.sweepOptions}>
        {options.map(opt => (
          <div key={opt.key}>
            <button
              type="button"
              style={choice === opt.key ? styles.sweepRowActive : styles.sweepRow}
              onClick={() => setChoice(opt.key)}
            >
              <span style={choice === opt.key ? styles.sweepRadioOn : styles.sweepRadio} aria-hidden="true" />
              <span style={styles.sweepRowLabel}>{opt.label}</span>
            </button>
            {choice === 'goal' && opt.key === 'goal' && (
              <select style={styles.sweepSelect} value={goalId} onChange={e => setGoalId(e.target.value)}>
                {unmetGoals.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name} — {formatCurrency(parseFloat(g.current_amount || 0))} of {formatCurrency(parseFloat(g.target_amount || 0))}
                  </option>
                ))}
              </select>
            )}
            {choice === 'category' && opt.key === 'category' && (
              <select style={styles.sweepSelect} value={catId} onChange={e => setCatId(e.target.value)}>
                {catOptions.map(o => (
                  <option key={o.id} value={o.id}>{o.parentName} · {o.name}</option>
                ))}
              </select>
            )}
            {choice === 'debt' && opt.key === 'debt' && (
              <select style={styles.sweepSelect} value={debtId} onChange={e => setDebtId(e.target.value)}>
                {debtOptions.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} — {formatCurrency(parseFloat(d.current_balance || 0))}
                  </option>
                ))}
              </select>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        style={{ ...styles.sweepApply, opacity: choice && !applying ? 1 : 0.5, cursor: choice && !applying ? 'pointer' : 'not-allowed' }}
        onClick={apply}
        disabled={!choice || applying}
      >
        {applying ? 'Applying…' : 'Apply and finish'}
      </button>
    </section>
  )
}

async function bumpGoal(goalId, amount) {
  const { data: goal } = await supabase.from('savings_goals').select('current_amount').eq('id', goalId).maybeSingle()
  if (!goal) return
  const next = parseFloat(goal.current_amount || 0) + amount
  await supabase.from('savings_goals').update({ current_amount: next }).eq('id', goalId)
  emitDataEvent(EVENTS.GOAL_UPDATED)
}

async function sweepToDebt(debtId, amount) {
  const { data: debt } = await supabase.from('debts').select('current_balance').eq('id', debtId).maybeSingle()
  if (!debt) return
  const next = parseFloat(debt.current_balance || 0) - amount
  await supabase.from('debts').update({ current_balance: next, updated_at: new Date().toISOString() }).eq('id', debtId)
  // Record a debt_payments row with a null transaction_id so the debt's payment
  // history stays complete (this isn't a real logged transaction).
  await supabase.from('debt_payments').insert({
    debt_id: debtId, transaction_id: null,
    principal_amount: amount, interest_amount: 0, paid_at: new Date().toISOString(),
  })
  await recordDebtBalance(debtId, next, 'sweep')
  emitDataEvent(EVENTS.DEBT_UPDATED)
}

// Roll a leftover into NEXT month's equivalent category (relative to the
// reviewed month). Creates next month's budget row and/or the category if they
// don't exist yet; otherwise just bumps the existing limit.
async function rollToNextMonth(userId, chosen, amount, selMonth, selYear) {
  let nm = selMonth + 1
  let ny = selYear
  if (nm > 12) { nm = 1; ny += 1 }
  // Partner-aware budget lookup (matches Plan) so we reuse a shared budget the
  // partner may own instead of creating a duplicate for the same month.
  const { data: link } = await supabase.from('partner_links')
    .select('user_id_1, user_id_2')
    .or(`user_id_1.eq.${userId},user_id_2.eq.${userId}`).eq('is_active', true).maybeSingle()
  const pid = link ? (link.user_id_1 === userId ? link.user_id_2 : link.user_id_1) : null
  const userIds = [userId, ...(pid ? [pid] : [])]
  let { data: budget } = await supabase.from('budgets').select('id')
    .in('user_id', userIds).eq('month', nm).eq('year', ny).eq('is_active', true)
    .order('created_at').limit(1).maybeSingle()
  if (!budget) {
    const { data: created } = await supabase.from('budgets').insert({
      user_id: userId, month: nm, year: ny, is_active: true, total_amount: 0,
      name: `${MONTH_NAMES[nm - 1]} ${ny}`,
    }).select('id').single()
    budget = created
    if (budget) emitDataEvent(EVENTS.BUDGET_SAVED)
  }
  if (!budget) return
  const { data: nextCats } = await supabase.from('budget_categories')
    .select('id, name, parent_id, limit_amount').eq('budget_id', budget.id)
  const cats = nextCats || []
  const pNameLower = {}
  cats.forEach(c => { if (!c.parent_id) pNameLower[c.id] = (c.name || '').toLowerCase().trim() })
  const wantParent = (chosen.parentName || '').toLowerCase().trim()
  const wantChild = (chosen.name || '').toLowerCase().trim()
  const target = cats.find(c => c.parent_id &&
    (pNameLower[c.parent_id] || '') === wantParent &&
    (c.name || '').toLowerCase().trim() === wantChild)
  if (target) {
    await supabase.from('budget_categories')
      .update({ limit_amount: parseFloat(target.limit_amount || 0) + amount }).eq('id', target.id)
  } else {
    let parent = cats.find(c => !c.parent_id && (c.name || '').toLowerCase().trim() === wantParent)
    if (!parent && chosen.parentName) {
      const { data: np } = await supabase.from('budget_categories').insert({
        user_id: userId, budget_id: budget.id, name: chosen.parentName, icon: '💰', limit_amount: 0, sort_order: 0,
      }).select('id').single()
      parent = np
    }
    await supabase.from('budget_categories').insert({
      user_id: userId, budget_id: budget.id, name: chosen.name, icon: '💰',
      limit_amount: amount, parent_id: parent ? parent.id : null, sort_order: 0,
    })
  }
  emitDataEvent(EVENTS.CATEGORY_SAVED)
}

// ─── Styles ─────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  overlay: {
    position: 'fixed', inset: 0, overscrollBehaviorX: 'contain',
    background: 'rgba(30, 16, 64, 0.65)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
    fontFamily: f.body,
  },
  modal: {
    overscrollBehaviorX: 'contain',
    background: c.cream,
    borderRadius: '20px',
    width: '100%', maxWidth: '480px', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(30, 16, 64, 0.3)',
    overflow: 'hidden',
  },

  // Header — plum band at top
  header: {
    background: c.plum,
    color: c.cream,
    padding: '20px 24px 22px',
    flexShrink: 0,
  },
  headerInner: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: '14px',
  },
  headerKicker: {
    fontSize: '11px', fontWeight: 600,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: 'rgba(250, 247, 242, 0.6)',
    marginBottom: '6px',
  },
  monthSelect: {
    background: 'rgba(250, 247, 242, 0.1)',
    color: c.cream,
    border: 'none',
    padding: '6px 10px',
    borderRadius: '8px',
    fontFamily: f.display,
    fontSize: '20px', fontWeight: 600,
    letterSpacing: '-0.02em',
    outline: 'none', cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  closeBtn: {
    width: '34px', height: '34px',
    borderRadius: '50%',
    background: 'rgba(250, 247, 242, 0.1)',
    color: c.cream,
    border: 'none',
    display: 'grid', placeItems: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    fontSize: '20px', lineHeight: 1,
  },

  // Body
  body: {
    overscrollBehaviorX: 'contain',
    flex: 1, overflowY: 'auto',
    padding: '20px 24px',
  },
  loadingWrap: {
    padding: '60px 20px', textAlign: 'center',
  },
  loadingText: { fontSize: '14px', color: c.inkMuted },

  empty: {
    padding: '40px 20px', textAlign: 'center',
  },
  emptyTitle: {
    fontFamily: f.display, fontSize: '18px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em',
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '13.5px', color: c.inkMuted, lineHeight: 1.55,
  },

  // Hero (verdict)
  hero: {
    padding: '4px 0 22px',
    borderBottom: `1px solid ${c.line}`,
    marginBottom: '4px',
  },
  heroKicker: {
    fontSize: '11px', fontWeight: 600,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: c.inkMuted,
    marginBottom: '8px',
  },
  heroValue: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '46px', letterSpacing: '-0.03em',
    color: c.plum, lineHeight: 1.05,
    marginBottom: '6px',
    fontVariantNumeric: 'tabular-nums',
    width: 'fit-content',
  },
  heroSub: {
    fontSize: '13.5px', color: c.plumSoft,
    fontWeight: 500,
  },

  // Section
  section: {
    padding: '18px 0',
    borderBottom: `1px solid ${c.line}`,
  },
  sectionLabel: {
    fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: c.inkMuted,
    marginBottom: '14px',
  },

  // Category list
  catList: {
    display: 'flex', flexDirection: 'column', gap: '14px',
  },
  catRow: {
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  catRowTop: {
    display: 'flex', alignItems: 'baseline', gap: '8px',
  },
  catIconUser: {
    fontSize: '14px',
  },
  catName: {
    flex: 1, minWidth: 0,
    fontSize: '14px', fontWeight: 600,
    color: c.plum,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  catAmount: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '15px', color: c.plum,
    letterSpacing: '-0.015em',
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
  },
  catBar: {
    height: '4px',
    background: c.cream3,
    borderRadius: '2px',
    overflow: 'hidden',
  },
  catBarFill: {
    height: '100%', borderRadius: '2px',
    transition: 'width 300ms ease',
  },
  catMeta: {
    fontSize: '11.5px', color: c.inkMuted,
  },

  // Bills
  billsAllPaid: {
    fontSize: '14px', color: c.green,
    fontWeight: 600, lineHeight: 1.5,
  },
  billsMissed: {
    fontSize: '13.5px', color: c.plumSoft,
    marginBottom: '10px', lineHeight: 1.5,
  },
  missedList: {
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  missedRow: {
    display: 'flex', justifyContent: 'space-between',
    padding: '8px 12px',
    background: c.orangeSoft,
    border: `1px solid rgba(217, 119, 6, 0.15)`,
    borderRadius: '8px',
    fontSize: '13px',
  },
  missedName: {
    color: c.plum, fontWeight: 600,
  },
  missedAmount: {
    fontFamily: f.display, fontWeight: 600,
    color: c.orange, letterSpacing: '-0.01em',
    fontVariantNumeric: 'tabular-nums',
  },

  // Goals & debts
  gdList: {
    display: 'flex', flexDirection: 'column', gap: '14px',
  },
  gdRow: {
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  gdTop: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    gap: '8px',
  },
  gdName: {
    fontSize: '14px', fontWeight: 600,
    color: c.plum,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  gdStatus: {
    fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    flexShrink: 0,
  },
  gdMeta: {
    fontSize: '11.5px', color: c.inkMuted,
  },

  // Observation
  observation: {
    padding: '12px 14px',
    borderRadius: '10px',
    fontSize: '13.5px', lineHeight: 1.55,
    fontWeight: 500,
  },
  observationCaution: {
    background: c.orangeSoft, color: c.orange,
  },
  observationPositive: {
    background: c.greenSoft, color: c.green,
  },

  // Simple-mode debt reminder + Leftover Sweep (Chunk 8)
  sweepSub: {
    fontSize: '13px', color: c.plumSoft, lineHeight: 1.5, marginBottom: '12px',
  },
  simpleDebtList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  simpleDebtRow: { display: 'flex', flexDirection: 'column', gap: '6px' },
  simpleDebtName: { fontSize: '14px', fontWeight: 600, color: c.plum },
  simpleDebtControls: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  simpleDebtCur: { fontSize: '12px', color: c.inkMuted, fontVariantNumeric: 'tabular-nums' },
  simpleDebtInput: {
    flex: 1, minWidth: '90px', padding: '8px 10px', borderRadius: '9px',
    border: `1px solid ${c.lineStrong}`, fontFamily: f.body, fontSize: '14px',
    color: c.plum, background: c.white, fontVariantNumeric: 'tabular-nums',
  },
  simpleDebtBtn: {
    padding: '8px 14px', borderRadius: '9px', border: 'none',
    background: c.plum, color: c.cream, fontFamily: f.body, fontSize: '13px',
    fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  },
  simpleDebtBtnDone: {
    padding: '8px 14px', borderRadius: '9px', border: `1px solid ${c.green}`,
    background: c.greenSoft, color: c.green, fontFamily: f.body, fontSize: '13px',
    fontWeight: 600, cursor: 'default', flexShrink: 0,
  },
  skipAllBtn: {
    marginTop: '12px', background: 'transparent', border: 'none',
    color: c.inkMuted, fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer', padding: '4px 0',
  },
  sweepHead: {
    fontFamily: f.display, fontSize: '18px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em', marginBottom: '4px',
    fontVariantNumeric: 'tabular-nums',
  },
  sweepOptions: { display: 'flex', flexDirection: 'column', gap: '8px' },
  sweepRow: {
    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
    padding: '12px 14px', borderRadius: '11px', textAlign: 'left',
    background: c.cream2, border: `1px solid ${c.line}`,
    fontFamily: f.body, fontSize: '14px', fontWeight: 500, color: c.plum, cursor: 'pointer',
  },
  sweepRowActive: {
    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
    padding: '12px 14px', borderRadius: '11px', textAlign: 'left',
    background: c.greenSoft, border: `1px solid rgba(22, 163, 74, 0.35)`,
    fontFamily: f.body, fontSize: '14px', fontWeight: 600, color: c.plum, cursor: 'pointer',
  },
  sweepRadio: {
    width: '16px', height: '16px', borderRadius: '50%',
    border: `2px solid ${c.lineStrong}`, flexShrink: 0,
  },
  sweepRadioOn: {
    width: '16px', height: '16px', borderRadius: '50%',
    border: `5px solid ${c.green}`, background: c.white, flexShrink: 0,
  },
  sweepRowLabel: { flex: 1, minWidth: 0 },
  sweepSelect: {
    marginTop: '8px', width: '100%', padding: '10px 12px', borderRadius: '10px',
    border: `1px solid ${c.lineStrong}`, fontFamily: f.body, fontSize: '14px',
    color: c.plum, background: c.white,
  },
  sweepApply: {
    marginTop: '16px', width: '100%', padding: '13px', borderRadius: '12px',
    border: 'none', background: c.green, color: '#FFFFFF',
    fontFamily: f.body, fontSize: '15px', fontWeight: 600,
  },

  // Footer
  footer: {
    padding: '14px 22px calc(env(safe-area-inset-bottom, 16px) + 14px)',
    borderTop: `1px solid ${c.line}`,
    background: c.cream,
    display: 'flex', gap: '10px', alignItems: 'center',
    flexShrink: 0,
  },
  footerSecondary: {
    flex: 1,
    padding: '11px',
    background: 'transparent',
    color: c.plumSoft,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '5px',
  },
  footerPrimary: {
    flex: 1,
    padding: '11px',
    background: c.plum,
    color: c.cream,
    border: 'none',
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
  },
}
